import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import Swal from "sweetalert2";

import { db, storage } from "../../firebase";
import css from "./blogBannerAdmin.module.css";

const DEFAULT_FORM = {
  title: "",
  description: "",
  buttonText: "",
  buttonLink: "",
  imageUrl: "",
  imagePath: "",
  imageAlt: "",
};

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

export default function BlogBannerAdmin() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [selectedFile, setSelectedFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadBanner = async () => {
      try {
        const snapshot = await getDoc(doc(db, "settings", "blogBanner"));

        if (snapshot.exists()) {
          setForm({
            ...DEFAULT_FORM,
            ...snapshot.data(),
          });
        }
      } catch (error) {
        console.error("Помилка завантаження банера:", error);

        Swal.fire({
          icon: "error",
          title: "Помилка",
          text: "Не вдалося завантажити дані банера.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadBanner();
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      setLocalPreview("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(selectedFile);
    setLocalPreview(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [selectedFile]);

  const previewImage = useMemo(() => {
    return localPreview || form.imageUrl || "";
  }, [localPreview, form.imageUrl]);

  const handleFieldChange = (field, value) => {
    setForm((previousForm) => ({
      ...previousForm,
      [field]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      Swal.fire({
        icon: "error",
        title: "Неправильний файл",
        text: "Оберіть зображення у форматі JPG, PNG або WebP.",
      });

      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      Swal.fire({
        icon: "error",
        title: "Файл завеликий",
        text: "Максимальний розмір зображення — 5 МБ.",
      });

      event.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const uploadNewImage = async () => {
    if (!selectedFile) {
      return {
        imageUrl: form.imageUrl,
        imagePath: form.imagePath,
      };
    }

    const safeFileName = selectedFile.name
      .replace(/[^a-zA-Z0-9._-]/g, "-")
      .toLowerCase();

    const imagePath = `settings/blog-banner/${Date.now()}-${safeFileName}`;
    const imageRef = ref(storage, imagePath);

    await uploadBytes(imageRef, selectedFile, {
      contentType: selectedFile.type,
    });

    const imageUrl = await getDownloadURL(imageRef);

    return {
      imageUrl,
      imagePath,
    };
  };

  const removeOldImage = async (oldImagePath, newImagePath) => {
    if (!oldImagePath || oldImagePath === newImagePath) {
      return;
    }

    try {
      await deleteObject(ref(storage, oldImagePath));
    } catch (error) {
      // Якщо старого файла вже немає, це не повинно ламати збереження.
      console.warn("Не вдалося видалити старе зображення:", error);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const title = form.title.trim();
    const description = form.description.trim();
    const buttonText = form.buttonText.trim();
    const buttonLink = form.buttonLink.trim();
    const imageAlt = form.imageAlt.trim();

    if (!title) {
      Swal.fire({
        icon: "error",
        title: "Вкажіть заголовок",
        text: "Заголовок банера є обов’язковим.",
      });

      return;
    }

    if ((buttonText && !buttonLink) || (!buttonText && buttonLink)) {
      Swal.fire({
        icon: "error",
        title: "Перевірте кнопку",
        text: "Текст кнопки та посилання потрібно заповнювати разом.",
      });

      return;
    }

    setIsSaving(true);

    try {
      const oldImagePath = form.imagePath || "";
      const uploadedImage = await uploadNewImage();

      const dataForSave = {
        title,
        description,
        buttonText,
        buttonLink,
        imageAlt,
        imageUrl: uploadedImage.imageUrl || "",
        imagePath: uploadedImage.imagePath || "",
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "settings", "blogBanner"), dataForSave, {
        merge: true,
      });

      await removeOldImage(oldImagePath, uploadedImage.imagePath);

      setForm((previousForm) => ({
        ...previousForm,
        ...dataForSave,
      }));

      setSelectedFile(null);
      setLocalPreview("");

      await Swal.fire({
        icon: "success",
        title: "Банер оновлено",
        text: "Зміни успішно збережено.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Помилка збереження банера:", error);

      Swal.fire({
        icon: "error",
        title: "Помилка",
        text: "Не вдалося зберегти банер.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className={css.loading}>Завантаження банера...</div>;
  }

  return (
    <section className={css.page}>
      <div className={css.container}>
        <div className={css.header}>
          <div>
            <p className={css.eyebrow}>Налаштування блогу</p>

            <h1 className={css.title}>Банер блогу</h1>

            <p className={css.subtitle}>
              Змініть зображення, заголовок, опис, текст кнопки та посилання.
            </p>
          </div>
        </div>

        <div className={css.layout}>
          <form className={css.form} onSubmit={handleSave}>
            <div className={css.formGroup}>
              <label className={css.label} htmlFor="blog-banner-title">
                Заголовок
                <span className={css.required}>*</span>
              </label>

              <input
                id="blog-banner-title"
                type="text"
                className={css.input}
                value={form.title}
                onChange={(event) =>
                  handleFieldChange("title", event.target.value)
                }
                placeholder="Заголовок банера"
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.label} htmlFor="blog-banner-description">
                Опис
              </label>

              <textarea
                id="blog-banner-description"
                className={css.textarea}
                rows={5}
                value={form.description}
                onChange={(event) =>
                  handleFieldChange("description", event.target.value)
                }
                placeholder="Короткий опис банера"
              />
            </div>

            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.label} htmlFor="blog-banner-button-text">
                  Текст кнопки
                </label>

                <input
                  id="blog-banner-button-text"
                  type="text"
                  className={css.input}
                  value={form.buttonText}
                  onChange={(event) =>
                    handleFieldChange("buttonText", event.target.value)
                  }
                  placeholder="Наприклад: Читати"
                />
              </div>

              <div className={css.formGroup}>
                <label className={css.label} htmlFor="blog-banner-button-link">
                  Посилання кнопки
                </label>

                <input
                  id="blog-banner-button-link"
                  type="text"
                  className={css.input}
                  value={form.buttonLink}
                  onChange={(event) =>
                    handleFieldChange("buttonLink", event.target.value)
                  }
                  placeholder="/blog/article або https://..."
                />
              </div>
            </div>

            <div className={css.formGroup}>
              <label className={css.label} htmlFor="blog-banner-image-alt">
                Alt зображення
              </label>

              <input
                id="blog-banner-image-alt"
                type="text"
                className={css.input}
                value={form.imageAlt}
                onChange={(event) =>
                  handleFieldChange("imageAlt", event.target.value)
                }
                placeholder="Опис зображення для SEO"
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.label} htmlFor="blog-banner-image">
                Зображення банера
              </label>

              <label className={css.fileButton} htmlFor="blog-banner-image">
                Обрати зображення
              </label>

              <input
                id="blog-banner-image"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className={css.fileInput}
                onChange={handleFileChange}
              />

              <p className={css.fileHint}>JPG, PNG або WebP. Максимум 5 МБ.</p>

              {selectedFile && (
                <p className={css.selectedFile}>Обрано: {selectedFile.name}</p>
              )}
            </div>

            <div className={css.actions}>
              <button
                type="submit"
                className={css.saveButton}
                disabled={isSaving}
              >
                {isSaving ? "Збереження..." : "Зберегти банер"}
              </button>
            </div>
          </form>

          <aside className={css.previewPanel}>
            <h2 className={css.previewTitle}>Попередній перегляд</h2>

            <div className={css.previewBanner}>
              <div className={css.previewText}>
                <h3>{form.title || "Заголовок банера"}</h3>

                {form.description && <p>{form.description}</p>}

                {form.buttonText && (
                  <span className={css.previewButton}>{form.buttonText}</span>
                )}
              </div>

              <div className={css.previewImageWrap}>
                {previewImage ? (
                  <img src={previewImage} alt={form.imageAlt || form.title} />
                ) : (
                  <div className={css.emptyPreview}>Зображення не вибрано</div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
